import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Coins, Minus, ArrowLeft, Loader2, User as UserIcon, AlertCircle } from 'lucide-react';
import { apiRequest } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function AdminLoyaltyScanPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const [customer, setCustomer] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [points, setPoints] = useState('');
    const [reason, setReason] = useState('Utilisation en magasin');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            navigate(`/auth?redirect=/admin/loyalty/scan/${token}`);
            return;
        }
        loadCustomer();
    }, [user, authLoading, token]);

    const loadCustomer = async () => {
        setLoading(true);
        try {
            const data = await apiRequest(`/admin/loyalty/qr-lookup/${token}`);
            setCustomer(data);
        } catch (e) {
            // Backend enforces admin auth (returns 403 if not admin, 404 if token unknown)
            const msg = (e.message || '').toLowerCase();
            if (msg.includes('admin') || msg.includes('autoris') || msg.includes('forbidden') || msg.includes('403')) {
                setError("Accès réservé à l'administrateur. Connectez-vous avec un compte admin.");
            } else {
                setError(e.message || 'Code QR invalide');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = async () => {
        const amount = parseInt(points, 10);
        if (!Number.isFinite(amount) || amount <= 0) {
            toast.error('Nombre de points invalide');
            return;
        }
        setSubmitting(true);
        try {
            const result = await apiRequest('/admin/loyalty/remove-points', {
                method: 'POST',
                body: JSON.stringify({
                    user_id: customer.user_id,
                    points: amount,
                    reason: reason || 'Utilisation en magasin',
                }),
            });
            toast.success(result.message);
            // Refresh balance
            setCustomer((c) => ({ ...c, loyalty_points: result.new_balance }));
            setPoints('');
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || authLoading) {
        return (
            <div className="min-h-screen pt-20 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen pt-20 pb-12">
                <div className="max-w-md mx-auto px-4">
                    <Card className="glass rounded-3xl border-red-300/50">
                        <CardContent className="pt-8 text-center space-y-4">
                            <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
                            <h2 className="font-heading text-xl font-bold">Code QR non reconnu</h2>
                            <p className="text-muted-foreground text-sm">{error}</p>
                            <Link to="/admin">
                                <Button variant="outline" className="rounded-full" data-testid="back-to-admin-btn">
                                    <ArrowLeft className="h-4 w-4 mr-2" />
                                    Retour à l'admin
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="admin-loyalty-scan-page">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6">
                    <ArrowLeft className="h-4 w-4" />
                    Retour à l'admin
                </Link>

                <Card className="glass rounded-3xl mb-6 overflow-hidden">
                    <div className="bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-amber-500/5 p-6">
                        <div className="flex items-center gap-4">
                            {customer.picture ? (
                                <img src={customer.picture} alt={customer.name} className="h-16 w-16 rounded-full border-2 border-white" />
                            ) : (
                                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                                    <UserIcon className="h-8 w-8 text-primary" />
                                </div>
                            )}
                            <div className="flex-1">
                                <h2 className="font-heading text-xl font-bold" data-testid="scan-customer-name">{customer.name}</h2>
                                <p className="text-sm text-muted-foreground">{customer.email}</p>
                            </div>
                            <div className="text-right">
                                <div className="flex items-center gap-2 text-amber-500 justify-end">
                                    <Coins className="h-6 w-6" />
                                    <span className="font-bold text-3xl" data-testid="scan-customer-balance">{customer.loyalty_points}</span>
                                </div>
                                <p className="text-xs text-muted-foreground">= {customer.loyalty_points}€</p>
                            </div>
                        </div>
                    </div>
                </Card>

                <Card className="glass rounded-3xl">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Minus className="h-5 w-5 text-red-500" />
                            Retirer des points (paiement comptoir)
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label>Points à retirer</Label>
                            <Input
                                type="number"
                                min="1"
                                max={customer.loyalty_points}
                                value={points}
                                onChange={(e) => setPoints(e.target.value)}
                                placeholder={`Max ${customer.loyalty_points}`}
                                className="mt-1 text-lg"
                                autoFocus
                                data-testid="scan-points-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                1 point = 1€ de réduction. Solde après retrait : <strong>{Math.max(0, customer.loyalty_points - (parseInt(points, 10) || 0))} pts</strong>
                            </p>
                        </div>
                        <div>
                            <Label>Raison</Label>
                            <Input
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Utilisation en magasin"
                                className="mt-1"
                                data-testid="scan-reason-input"
                            />
                        </div>
                        <Button
                            onClick={handleRemove}
                            disabled={submitting || !points || parseInt(points, 10) <= 0 || parseInt(points, 10) > customer.loyalty_points}
                            className="w-full rounded-full bg-red-500 hover:bg-red-600 text-white text-base h-12"
                            data-testid="scan-submit-btn"
                        >
                            {submitting ? (
                                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                            ) : (
                                <Minus className="h-5 w-5 mr-2" />
                            )}
                            Retirer {points || 0} points
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
